import { ApplicantType, useApplication } from "@/api/application/application";
import { CountrySelect } from "@/components/UI/forms/CountrySelect";
import { SubdivisionSelect, hasSubdivisions } from "@/components/UI/forms/SubdivisionSelect";
import { BoolRadioCards, RadioCards } from "@/components/UI/forms/RadioCards";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Checkbox,
  Collapse,
  Flex,
  FormHelperText,
  Heading,
  Link,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";

import { useLoggedInUser } from "@/api/user/user";
import { FieldController } from "@/components/UI/forms/FieldController";
import { DateInput } from "@/components/UI/forms/DateInput";
import { Control, Controller, UseFormSetValue, useWatch } from "react-hook-form";
import { RegistryFormData } from "..";
import { booleanValidator, minAgeValidator } from "@/utils/formUtils";
import { InputText } from "@/components/UI/forms/InputText";
import { useTranslation, Trans } from "react-i18next";

type LoadedProfileFormProps = {
  isLocked: boolean;
  isSubmitted: boolean;
  formControl: Control<RegistryFormData>;
  setValue: UseFormSetValue<RegistryFormData>;
  applicantType: string;
};
const LoadedProfileForm: React.FC<LoadedProfileFormProps> = ({
  isLocked,
  isSubmitted,
  formControl,
  setValue,
  applicantType,
}) => {
  const isReadOnly = isSubmitted || isLocked;
  const submissionType = formControl._formValues?.application?.applicant_type;
  const selfSubmit = submissionType === "self";
  const { t } = useTranslation(["registry", "common", "validation"]);
  const supportEmail = t("common:emails.support");

  const applicantCountryField =
    applicantType == "proxy" ? "application.proxy_country" : "application.patient_country";
  const applicantStateField =
    applicantType == "proxy" ? "application.proxy_state" : "application.patient_state";

  const applicantCountry = useWatch({ control: formControl, name: applicantCountryField });
  const patientCountry = useWatch({ control: formControl, name: "application.patient_country" });

  return (
    <VStack
      paddingBlock={[0, 4, 8]}
      spacing={10}
      maxWidth="container.md"
      margin="auto"
      alignItems="flex-start"
      marginBlockEnd={0}
    >
      <Heading fontSize={["2xl", "3xl", "4xl"]} fontWeight="regular">
        <Text
          fontSize="md"
          as="div"
          textTransform="uppercase"
          color="gray.600"
          fontWeight="semibold"
        >
          {t("registry:general.stepLabel")}
        </Text>
        {t("registry:general.heading")}
      </Heading>

      {isReadOnly && (
        <Alert
          padding={6}
          borderRadius="md"
          backgroundColor="gray.50"
          borderColor="gray.200"
          borderWidth="1px"
          status="info"
          fontSize="lg"
        >
          <AlertIcon boxSize={6} marginInlineStart={2} marginInlineEnd={6} />
          <AlertDescription>
            <Trans
              i18nKey="registry:general.lockedAlert"
              values={{ email: supportEmail }}
              components={{
                email: <Link textDecoration="underline" href={`mailto:${supportEmail}`} />,
              }}
            />
          </AlertDescription>
        </Alert>
      )}

      <Stack width="full">
        <FieldController
          control={formControl}
          name="application.applicant_type"
          rules={{
            required: t("validation:general.applicantTypeRequired"),
          }}
          render={({ field }) => (
            <RadioCards
              {...field}
              options={[
                { label: t("registry:general.optionYou"), value: "self" },
                { label: t("registry:general.optionSomeoneElse"), value: "proxy" },
              ]}
              value={field.value || undefined}
              size="xl"
              label={t("registry:general.whoIsApplicationFor")}
              isRequired
              disabled={isReadOnly}
            />
          )}
        />
      </Stack>

      <Heading margin={0} fontSize={["lg", "xl", "2xl"]} fontWeight="medium">
        {t("registry:general.yourInformation")}
      </Heading>

      <FieldController
        key={submissionType === "proxy" ? "proxy-country" : "patient-country"}
        control={formControl}
        name={applicantCountryField}
        label={t("registry:general.selectCountry")}
        disabled={isReadOnly}
        rules={{
          required: t("validation:general.countryRequired"),
        }}
        render={({ field }) => (
          <>
            <CountrySelect
              {...field}
              dataTestPrefix="profile-form-country"
              value={field.value || undefined}
              onChange={(value) => {
                field.onChange(value);
                // A subdivision only means anything alongside the country it
                // belongs to, so drop it rather than submit a mismatched pair.
                setValue(applicantStateField, null, { shouldValidate: true });
              }}
            />
            <FormHelperText fontSize="md" color="gray.800">
              {t("registry:general.countryHelperText")}
            </FormHelperText>
          </>
        )}
      />

      {hasSubdivisions(applicantCountry) && (
        <FieldController
          key={submissionType === "proxy" ? "proxy-state" : "patient-state"}
          control={formControl}
          name={applicantStateField}
          label={t("registry:general.selectState")}
          disabled={isReadOnly}
          rules={{
            required: t("validation:general.stateRequired"),
          }}
          render={({ field }) => (
            <SubdivisionSelect
              {...field}
              countryIso={applicantCountry as string}
              dataTestPrefix="profile-form-state"
              value={field.value || undefined}
            />
          )}
        />
      )}

      {/* Travel willingness follows the patient's location, so for proxy
          applicants it renders in the patient section below instead. */}
      {submissionType !== "proxy" && (
        <FieldController
          control={formControl}
          name="application.personal_travel_willing"
          disabled={isReadOnly}
          rules={{
            validate: booleanValidator(t("validation:general.travelWillingRequired")),
          }}
          render={({ field }) => (
            <BoolRadioCards
              {...field}
              size="sm"
              label={t("registry:general.willingToTravel")}
              isRequired
            />
          )}
        />
      )}

      <FieldController
        key={submissionType === "proxy" ? "proxy-date-of-birth" : "patient-date-of-birth"}
        control={formControl}
        name={
          applicantType == "proxy"
            ? "application.proxy_date_of_birth"
            : "application.patient_date_of_birth"
        }
        disabled={isReadOnly}
        rules={{
          validate: minAgeValidator(t("validation:general.mustBe18"), 18, true),
          required: t("validation:general.dateOfBirthRequired"),
        }}
        render={({ field }) => (
          <DateInput
            {...field}
            value={field.value || undefined}
            onChange={(value) => {
              field.onChange(value);
            }}
            dataTestId="profile-form-date-of-birth"
            label={t("registry:general.yourBirthdate")}
            isRequired
            autocomplete="bday"
            helperText={t("registry:general.birthdateHelperText")}
          />
        )}
      />

      <Flex width={"100%"} gap={4} flexDirection={["column", "row"]}>
        <FieldController
          key={submissionType === "proxy" ? "proxy-first-name" : "patient-first-name"}
          name={
            submissionType == "proxy"
              ? "application.proxy_first_name"
              : "application.patient_first_name"
          }
          control={formControl}
          rules={{ required: t("validation:general.firstNameRequired") }}
          disabled={isReadOnly}
          label={t("registry:general.yourFirstName")}
          render={({ field }) => {
            return (
              <InputText
                {...field}
                dataTestId="your-first-name"
                placeholder={t("registry:general.yourFirstNamePlaceholder")}
                value={field.value || undefined}
                disabled={isReadOnly}
              />
            );
          }}
        />

        <FieldController
          key={submissionType === "proxy" ? "proxy-last-name" : "patient-last-name"}
          name={
            submissionType == "proxy"
              ? "application.proxy_last_name"
              : "application.patient_last_name"
          }
          control={formControl}
          rules={{ required: t("validation:general.lastNameRequired") }}
          disabled={isReadOnly}
          label={t("registry:general.yourLastName")}
          render={({ field }) => (
            <InputText
              {...field}
              dataTestId="your-last-name"
              placeholder={t("registry:general.yourLastNamePlaceholder")}
              value={field.value || undefined}
              disabled={isReadOnly}
            />
          )}
        />
      </Flex>

      <FieldController
        key={submissionType === "proxy" ? "proxy-phone" : "patient-phone"}
        name={submissionType == "proxy" ? "application.proxy_phone" : "application.patient_phone"}
        control={formControl}
        rules={{ required: t("validation:general.phoneRequired") }}
        disabled={isReadOnly}
        label={t("registry:general.yourPhoneNumber")}
        render={({ field }) => (
          <InputText
            {...field}
            dataTestId="your-phone-number"
            placeholder={t("registry:general.yourPhoneNumberPlaceholder")}
            value={field.value || undefined}
            disabled={isReadOnly}
          />
        )}
      />

      {submissionType === "proxy" && (
        <>
          <Heading marginBlockStart={8} fontSize={["lg", "xl", "2xl"]} fontWeight="medum">
            {t("registry:general.patientInformation")}
          </Heading>

          <Flex width={"100%"} gap={4} flexDirection={["column", "row"]}>
            <FieldController
              name="application.patient_first_name"
              control={formControl}
              rules={{ required: t("validation:general.patientFirstNameRequired") }}
              disabled={isReadOnly}
              label={t("registry:general.patientFirstName")}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder={t("registry:general.patientFirstNamePlaceholder")}
                  value={field.value || undefined}
                  disabled={isReadOnly}
                />
              )}
            />

            <FieldController
              name="application.patient_last_name"
              control={formControl}
              rules={{ required: t("validation:general.patientLastNameRequired") }}
              disabled={isReadOnly}
              label={t("registry:general.patientLastName")}
              render={({ field }) => (
                <InputText
                  {...field}
                  placeholder={t("registry:general.patientLastNamePlaceholder")}
                  value={field.value || undefined}
                  disabled={isReadOnly}
                />
              )}
            />
          </Flex>

          <FieldController
            name="application.patient_date_of_birth"
            control={formControl}
            rules={{
              validate: minAgeValidator(t("validation:general.patientMustBe18"), 18, true),
              required: t("validation:general.patientDateOfBirthRequired"),
            }}
            disabled={isReadOnly}
            render={({ field }) => (
              <DateInput
                {...field}
                value={field.value || undefined}
                disabled={isReadOnly}
                dataTestId="personal-form-patient-date-of-birth"
                label={t("registry:general.patientBirthdate")}
                isRequired
              />
            )}
          />

          <FieldController
            name="application.patient_country"
            control={formControl}
            label={t("registry:general.patientCountry")}
            disabled={isReadOnly}
            rules={{
              required: t("validation:general.patientCountryRequired"),
            }}
            render={({ field }) => (
              <CountrySelect
                {...field}
                dataTestPrefix="profile-form-patient-country"
                value={field.value || undefined}
                onChange={(value) => {
                  field.onChange(value);
                  setValue("application.patient_state", null, { shouldValidate: true });
                }}
              />
            )}
          />

          {hasSubdivisions(patientCountry) && (
            <FieldController
              name="application.patient_state"
              control={formControl}
              label={t("registry:general.patientState")}
              disabled={isReadOnly}
              rules={{
                required: t("validation:general.patientStateRequired"),
              }}
              render={({ field }) => (
                <SubdivisionSelect
                  {...field}
                  countryIso={patientCountry as string}
                  dataTestPrefix="profile-form-patient-state"
                  value={field.value || undefined}
                />
              )}
            />
          )}

          <FieldController
            control={formControl}
            name="application.personal_travel_willing"
            disabled={isReadOnly}
            rules={{
              validate: booleanValidator(t("validation:general.patientTravelWillingRequired")),
            }}
            render={({ field }) => (
              <BoolRadioCards
                {...field}
                size="sm"
                label={t("registry:general.patientWillingToTravel")}
                isRequired
              />
            )}
          />

          <FieldController
            name="application.patient_phone"
            control={formControl}
            rules={{ required: t("validation:general.patientPhoneRequired") }}
            disabled={isReadOnly}
            label={t("registry:general.patientPhoneNumber")}
            render={({ field }) => (
              <InputText
                {...field}
                dataTestId="patient-phone-number"
                placeholder={t("registry:general.patientPhoneNumberPlaceholder")}
                value={field.value || undefined}
                disabled={isReadOnly}
              />
            )}
          />

          <Stack width="full" spacing={0}>
            <FieldController
              name="application.personal_patient_email"
              control={formControl}
              rules={{
                validate: (value, formValues) => {
                  const noEmailSelected = formValues?.application?.personal_no_patient_email;
                  if (!value && !noEmailSelected) {
                    return t("validation:general.patientEmailRequired");
                  }
                  if (value && noEmailSelected) {
                    return t("validation:general.patientEmailConflict");
                  }
                  return true;
                },
              }}
              disabled={isReadOnly}
              label={t("registry:general.patientEmail")}
              render={({ field }) => (
                <InputText
                  {...field}
                  type="email"
                  placeholder={t("registry:general.patientEmailPlaceholder")}
                  value={field.value || undefined}
                  disabled={
                    isReadOnly || formControl._formValues?.application?.personal_no_patient_email
                  }
                />
              )}
            />

            <FieldController
              name="application.personal_no_patient_email"
              control={formControl}
              hideRequiredAstrisk
              rules={{
                validate: (value, formValues) => {
                  const email = formValues?.application?.personal_patient_email;
                  if (!value && !email) {
                    return t("validation:general.patientEmailRequired");
                  }
                  if (value && email) {
                    return t("validation:general.patientEmailConflict");
                  }
                  return true;
                },
              }}
              disabled={isSubmitted}
              label=""
              render={({ field }) => (
                <Checkbox
                  isChecked={!!field.value}
                  disabled={isSubmitted}
                  onChange={field.onChange}
                  fontSize="lg"
                  paddingBlockStart={2}
                  alignItems="baseline"
                  data-testid="application.personal_no_patient_email"
                >
                  <Text position="relative" top={-0.5}>
                    {t("registry:general.patientNoEmail")}
                  </Text>
                </Checkbox>
              )}
            />
          </Stack>
        </>
      )}

      <Stack width="full">
        <Collapse in={formControl._formValues?.application?.applicant_type === "proxy"}>
          <Text fontSize="lg">{t("registry:general.proxyCertification")}</Text>
        </Collapse>
      </Stack>
    </VStack>
  );
};

export const ProfileForm = (props: {
  formControl: Control<RegistryFormData>;
  setValue: UseFormSetValue<RegistryFormData>;
  isLocked: boolean;
  isSubmitted: boolean;
  applicantType: string;
}) => {
  const { isLocked, isSubmitted, formControl, setValue, applicantType } = props;
  const { t } = useTranslation("common");

  const applicationQuery = useApplication();
  const loginQuery = useLoggedInUser();

  // Show loading state while fetching application data
  if (applicationQuery.isLoading || loginQuery.isLoading) {
    return (
      <VStack
        paddingBlock={[0, 4, 8]}
        spacing={10}
        maxWidth="container.md"
        margin="auto"
        alignItems="center"
        marginBlockEnd={8}
      >
        <Text>{t("status.loadingApplication")}</Text>
      </VStack>
    );
  } else if (applicationQuery.isSuccess && loginQuery.isSuccess && loginQuery.data?.logged_in) {
    return (
      <LoadedProfileForm
        isLocked={isLocked}
        isSubmitted={isSubmitted}
        formControl={formControl}
        setValue={setValue}
        applicantType={applicantType}
      ></LoadedProfileForm>
    );
  } else {
    return <Alert>{t("errors.problemOccurred")}</Alert>;
  }
};
